// pages/secure/ResidentialSearch.js — residential searches on My Site Searches (homes).
//
// Owner, 26 Oct 2026: "setup a residential search for house just like the commercial search — in
// my portal — 2 bed/2 bath or 2 bd/1 bath or 3 bd/2 ba in Glenview, … Gurnee, include cities in
// (north)west Indiana: Crown Point IN, and group them according to cities and email to me daily
// and email sandrarossi77@outlook.com and run searches 3 times daily but email only new found."
//
//   ResidentialForm      create / edit: bed & bath combinations, kinds of home, sale / rent,
//                        price range, towns with state, how often, email new finds + recipients
//   ResidentialDetail    the results grouped by city (Illinois towns, then Indiana), with filters
//                        (sale / rent, bed & bath), CSV, "Send test email to me"
//   EmailSiteSwitch      the site switch for new-find emails (brokerage_settings, off by default)
//
// For sale by owner (owner, 1 Nov 2026: "look for sale by owner homes too"): the option "Include For
// Sale By Owner homes" (on by default; in Edit and as a switch on the search page), a "By owner"
// badge on those homes, the filter All / Agent-listed / By owner, and FSBO.com, ForSaleByOwner.com
// and Zillow FSBO lookups on by-owner homes (utils/listingLinks.js). They are leads to contact,
// by hand, respecting the Do-Not-Call rules — nothing here contacts anyone.
//
// Server: routes/brokerSiteSearch.js + lib/residentialSearch.js. Matching: bedrooms exactly as
// listed, bathrooms at least as listed (2 bd / 1.5 ba matches "2 bd / 1 ba") unless "exact" is on.
import React, { useCallback, useEffect, useState } from 'react';
import { useApiClient } from '../../hooks/useApiClient';
import Modal from '../../components/secure/Modal';
import LookupLinks, { AddressLink, LinkNote } from '../../components/shared/LookupLinks';
import { milesNote } from '../../utils/chicagoMiles';

const API = '/api/broker-site-searches';
const RES_USES = ['Single-family homes'];
export const FREQUENCY_LABEL = {
    thrice_daily: '3 times a day', daily: 'every day', weekly: 'weekly', biweekly: 'every other week', monthly: 'monthly', manual: 'only when I press Run',
};
export const FrequencyOptions = () => (
    <>
        <option value="thrice_daily">3 times a day (7 am, 1 pm, 7 pm)</option>
        <option value="daily">Every day</option>
        <option value="weekly">Weekly</option>
        <option value="biweekly">Every other week</option>
        <option value="monthly">Monthly</option>
        <option value="manual">Only when I press Run</option>
    </>
);
const KINDS = [['house', 'House (single-family)'], ['townhouse', 'Townhouse'], ['condo', 'Condo']];
const KIND_LABEL = { house: 'House', townhouse: 'Townhouse', condo: 'Condo' };
const LISTING = [['sale', 'For sale'], ['rent', 'For rent']];
const LISTING_LABEL = { sale: 'For sale', rent: 'For rent' };
const FSBO_HINT = 'For sale by owner: a prospecting lead. You may contact the owner to offer the brokerage\'s services — check the National Do Not Call Registry (and Indiana\'s list for Indiana numbers) before phoning; never text or auto-dial. The portal never contacts owners by itself.';
const CAND_STATUSES = ['new', 'shortlisted', 'toured', 'offer', 'rejected', 'off_market', 'closed'];
const PARKED = ['rejected', 'off_market', 'closed'];
const STATUS_LABEL = { off_market: 'off market' };
const STATE_ORDER = ['IL', 'IN'];
const lc = (s) => String(s || '').trim().toLowerCase();
const fmtDate = (d) => (d ? new Date(d).toLocaleString() : '—');
const pairLabel = (p) => `${Number(p.beds) === 0 ? 'Studio' : `${p.beds} bd`} / ${p.baths} ba`;
const bedsBaths = (c) => (c.beds == null && c.baths == null ? '—' : `${Number(c.beds) === 0 ? 'Studio' : `${c.beds ?? '?'} bd`} / ${c.baths ?? '?'} ba`);

/** Results by city: Illinois towns, then Indiana, then the rest; addresses in number order. */
export const groupByCity = (rows) => {
    const m = new Map();
    for (const c of rows) {
        const city = String(c.municipality || '').trim();
        const state = String(c.state || 'IL').toUpperCase();
        const key = city ? `${lc(city)}|${state}` : '~';
        if (!m.has(key)) m.set(key, { city: city || 'Town not known', state: city ? state : '', list: [] });
        m.get(key).list.push(c);
    }
    const rank = (g) => (!g.state ? 99 : STATE_ORDER.includes(g.state) ? STATE_ORDER.indexOf(g.state) : 50);
    return [...m.values()]
        .sort((a, b) => rank(a) - rank(b) || a.state.localeCompare(b.state) || a.city.localeCompare(b.city))
        .map((g) => ({ ...g, label: g.state ? `${g.city}, ${g.state}${milesNote(g.city, g.state)}` : g.city,
            list: g.list.sort((a, b) => String(a.address).localeCompare(String(b.address), 'en', { numeric: true })) }));
};

const applyFilters = (rows, f) => rows.filter((c) => {
    if (f.listing_type && c.listing_type !== f.listing_type) return false;
    // Seller: 'owner' = for sale by owner; 'agent' = for-sale homes listed by an agent (or not known).
    if (f.seller === 'owner' && c.seller_type !== 'owner') return false;
    if (f.seller === 'agent' && (c.seller_type === 'owner' || c.listing_type === 'rent')) return false;
    if (f.pair) {
        const [b, ba] = f.pair.split('/').map(Number);
        if (!(Number(c.beds) === b && Number(c.baths) >= ba)) return false;
    }
    if (f.town && lc(c.municipality) !== lc(f.town)) return false;
    if (f.status && c.status !== f.status) return false;
    if (!f.status && !f.showParked && PARKED.includes(c.status)) return false;
    if (f.newOnly && !c.is_new) return false;
    if (f.q && ![c.address, c.municipality, c.notes].join(' ').toLowerCase().includes(lc(f.q))) return false;
    return true;
});

// ---------------------------------------------------------------------------
// The site switch
// ---------------------------------------------------------------------------
export const EmailSiteSwitch = ({ canEdit }) => {
    const apiClient = useApiClient();
    const [s, setS] = useState(null);
    const [busy, setBusy] = useState(false);
    useEffect(() => { apiClient.get(`${API}/email-settings`).then(setS).catch(() => setS(null)); }, [apiClient]);
    if (!s) return null;
    const flip = async () => {
        setBusy(true);
        try { setS({ ...s, ...(await apiClient.put(`${API}/email-settings`, { enabled: !s.enabled })) }); }
        catch (err) { alert(err.message || 'Could not save.'); }
        finally { setBusy(false); }
    };
    return (
        <div className="flex flex-wrap items-center gap-2 rounded border border-gray-200 bg-white px-3 py-2 text-xs text-gray-700">
            <span className="font-semibold">Email settings:</span>
            <span>New-find emails for this site are <strong className={s.enabled ? 'text-green-700' : 'text-red-700'}>{s.enabled ? 'ON' : 'OFF'}</strong>.</span>
            <span className="text-gray-500">Each search also has its own switch and recipients (Edit). Only finds never emailed before are sent; nothing new means no email.</span>
            {!s.mail_configured && <span className="text-amber-700">Email is not configured on this site yet.</span>}
            {canEdit && (
                <button type="button" disabled={busy} onClick={flip} className="rounded border border-gray-300 px-2 py-0.5">
                    {s.enabled ? 'Turn off' : 'Turn on'}
                </button>
            )}
        </div>
    );
};

// ---------------------------------------------------------------------------
// Create / edit
// ---------------------------------------------------------------------------
const PairsEditor = ({ value, onChange }) => {
    const [beds, setBeds] = useState('2');
    const [baths, setBaths] = useState('1');
    const add = () => {
        const p = { beds: Number(beds), baths: Number(baths) };
        if (!Number.isFinite(p.beds) || !Number.isFinite(p.baths) || p.baths <= 0) return;
        if (!value.some((x) => Number(x.beds) === p.beds && Number(x.baths) === p.baths)) onChange([...value, p]);
    };
    return (
        <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-600">Bedrooms / bathrooms (any of these)</p>
            <p className="text-[11px] text-gray-500">Bedrooms exactly as listed; bathrooms at least as listed (2 bd / 1.5 ba counts as 2 bd / 1 ba) unless "exact" is ticked.</p>
            <div className="mt-1 flex flex-wrap gap-1">
                {value.map((p) => (
                    <button type="button" key={`${p.beds}/${p.baths}`} title="Click to remove"
                            onClick={() => onChange(value.filter((x) => !(Number(x.beds) === Number(p.beds) && Number(x.baths) === Number(p.baths))))}
                            className="rounded-full border border-slate-800 bg-slate-800 px-2 py-0.5 text-xs text-white">{pairLabel(p)} ×</button>
                ))}
                {!value.length && <span className="text-xs text-gray-400">None yet — add one below.</span>}
            </div>
            <div className="mt-1 flex flex-wrap items-center gap-2 text-sm">
                <select value={beds} onChange={(e) => setBeds(e.target.value)} className="rounded border border-gray-300 px-2 py-1">
                    {[0, 1, 2, 3, 4, 5, 6].map((n) => <option key={n} value={n}>{n === 0 ? 'Studio' : `${n} bd`}</option>)}
                </select>
                <span>/</span>
                <select value={baths} onChange={(e) => setBaths(e.target.value)} className="rounded border border-gray-300 px-2 py-1">
                    {[1, 1.5, 2, 2.5, 3, 3.5, 4].map((n) => <option key={n} value={n}>{n} ba</option>)}
                </select>
                <button type="button" onClick={add} className="rounded border border-gray-300 px-3 py-1 text-xs">Add</button>
            </div>
        </div>
    );
};

const TownsEditor = ({ value, onChange }) => {
    const [typed, setTyped] = useState('');
    const [state, setState] = useState('IL');
    const add = () => {
        const next = [...value];
        for (const part of typed.split(/[;\n]/).map((x) => x.trim()).filter(Boolean)) {
            const m = part.match(/^(.*?)[,\s]+([A-Za-z]{2})$/);
            const label = m ? `${m[1].replace(/,\s*$/, '').trim()}, ${m[2].toUpperCase()}` : `${part}, ${state}`;
            if (!next.some((v) => lc(v) === lc(label))) next.push(label);
        }
        onChange(next); setTyped('');
    };
    return (
        <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-600">Towns (with state)</p>
            <div className="mt-1 flex flex-wrap gap-1 rounded border border-gray-200 p-2">
                {value.map((t) => (
                    <button type="button" key={t} onClick={() => onChange(value.filter((v) => v !== t))} title="Click to remove"
                            className="rounded-full border border-slate-800 bg-slate-800 px-2 py-0.5 text-xs text-white">{t} ×</button>
                ))}
                {!value.length && <span className="text-xs text-gray-400">No towns yet.</span>}
            </div>
            <div className="mt-1 flex gap-2">
                <input value={typed} onChange={(e) => setTyped(e.target.value)} placeholder="e.g. Merrillville, IN — or several separated by ;"
                       onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
                       className="flex-1 rounded border border-gray-300 px-2 py-1 text-sm" />
                <select value={state} onChange={(e) => setState(e.target.value)} className="rounded border border-gray-300 px-2 py-1 text-sm" title="State when not typed">
                    <option value="IL">IL</option><option value="IN">IN</option><option value="WI">WI</option><option value="FL">FL</option>
                </select>
                <button type="button" onClick={add} className="rounded border border-gray-300 px-3 py-1 text-xs">Add</button>
            </div>
        </div>
    );
};

export const ResidentialForm = ({ initial, onSaved, onCancel }) => {
    const apiClient = useApiClient();
    const crit = (initial && initial.criteria) || {};
    const email = crit.email || {};
    const [f, setF] = useState({
        title: initial?.title || '',
        pairs: crit.pairs || [{ beds: 2, baths: 2 }, { beds: 2, baths: 1 }, { beds: 3, baths: 2 }],
        exact_baths: !!crit.exact_baths,
        kinds: crit.kinds || ['house'],
        listing_types: crit.listing_types || ['sale', 'rent'],
        price_min: crit.price_min || '',
        price_max: crit.price_max || '',
        towns: crit.towns || [],
        notes: crit.notes || '',
        // For sale by owner homes too (owner, 1 Nov 2026) — on unless this search switched it off.
        include_fsbo: crit.include_fsbo === undefined || crit.include_fsbo === null ? true : !!crit.include_fsbo,
        sweep_enabled: initial ? !!Number(initial.sweep_enabled) : true,
        sweep_frequency: initial?.sweep_frequency || 'thrice_daily',
        email_new_finds: initial ? !!email.enabled : false,
        email_recipients: (email.recipients || []).join(', '),
        // Which homes the email carries (owner, 26 Oct 2026: rentals only for now). Empty = all.
        email_listing_types: email.listing_types && email.listing_types.length ? email.listing_types : ['sale', 'rent'],
    });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const set = (k) => (e) => setF({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value });
    const toggleIn = (k, v) => setF({ ...f, [k]: f[k].includes(v) ? f[k].filter((x) => x !== v) : [...f[k], v] });

    const submit = async (e) => {
        e.preventDefault();
        setSaving(true); setError('');
        try {
            const body = { ...f, search_type: 'residential' };
            let id = initial && initial.search_id;
            if (initial) await apiClient.put(`${API}/${initial.search_id}`, body);
            else id = (await apiClient.post(API, body)).search_id;
            onSaved(id);
        } catch (err) {
            setError(err.message || 'Could not save.');
        } finally { setSaving(false); }
    };
    const field = 'mt-1 w-full rounded border border-gray-300 px-3 py-2 text-sm';
    const label = 'block text-xs font-semibold uppercase tracking-wide text-gray-600';
    return (
        <form onSubmit={submit} className="space-y-4">
            {error && <p className="rounded bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}
            <div>
                <label className={label}>Title</label>
                <input required className={field} value={f.title} onChange={set('title')} placeholder="Homes — North Shore, NW suburbs & Crown Point IN" />
            </div>
            <PairsEditor value={f.pairs} onChange={(pairs) => setF({ ...f, pairs })} />
            <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={f.exact_baths} onChange={set('exact_baths')} /> Exact bathrooms (2 bd / 1.5 ba does NOT count as 2 bd / 1 ba)</label>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                    <p className={label}>Kind of home</p>
                    <div className="mt-1 flex flex-wrap gap-3 text-sm">
                        {KINDS.map(([k, l]) => <label key={k} className="flex items-center gap-1"><input type="checkbox" checked={f.kinds.includes(k)} onChange={() => toggleIn('kinds', k)} /> {l}</label>)}
                    </div>
                </div>
                <div>
                    <p className={label}>For sale / for rent</p>
                    <div className="mt-1 flex flex-wrap gap-3 text-sm">
                        {LISTING.map(([k, l]) => <label key={k} className="flex items-center gap-1"><input type="checkbox" checked={f.listing_types.includes(k)} onChange={() => toggleIn('listing_types', k)} /> {l}</label>)}
                    </div>
                </div>
            </div>
            <label className={`flex items-start gap-2 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm ${f.listing_types.includes('sale') ? '' : 'opacity-60'}`}>
                <input type="checkbox" className="mt-1" checked={f.include_fsbo} onChange={set('include_fsbo')} />
                <span>
                    <span className="font-semibold">Include For Sale By Owner homes</span>
                    <span className="block text-[11px] text-gray-600">
                        Also looks on Zillow (by owner), FSBO.com, ForSaleByOwner.com and Craigslist (by owner) for homes for sale. They are
                        marked "By owner" and are included in the for-sale email as leads to contact. Only applies to homes for sale.
                    </span>
                </span>
            </label>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div><label className={label}>Lowest price ($, optional)</label><input className={field} value={f.price_min} onChange={set('price_min')} inputMode="numeric" /></div>
                <div><label className={label}>Highest price ($, optional)</label><input className={field} value={f.price_max} onChange={set('price_max')} inputMode="numeric" />
                    <p className="text-[11px] text-gray-500">Rentals are compared by monthly rent. Blank = any price.</p></div>
            </div>
            <TownsEditor value={f.towns} onChange={(towns) => setF({ ...f, towns })} />
            <div>
                <label className={label}>Notes</label>
                <textarea className={field} rows={2} value={f.notes} onChange={set('notes')} />
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.sweep_enabled} onChange={set('sweep_enabled')} /> Automatic search on</label>
                <select className="rounded border border-gray-300 px-2 py-1 text-sm" value={f.sweep_frequency} onChange={set('sweep_frequency')}><FrequencyOptions /></select>
            </div>
            <div className="rounded border border-gray-200 p-3">
                <label className="flex items-center gap-2 text-sm font-semibold"><input type="checkbox" checked={f.email_new_finds} onChange={set('email_new_finds')} /> Email new finds after each search</label>
                <p className="text-[11px] text-gray-500">One email per run with only the homes not emailed before, grouped by city. Nothing new = no email. Also needs the site's email switch (top of My Site Searches).</p>
                <label className={`${label} mt-2`}>Send to (comma-separated)</label>
                <input className={field} value={f.email_recipients} onChange={set('email_recipients')} placeholder="you@example.com, someone@example.com" />
                <div className="mt-2 flex flex-wrap items-center gap-3 text-sm">
                    <span className="text-xs font-semibold text-gray-600">Email these homes:</span>
                    {LISTING.map(([k, l]) => <label key={k} className="flex items-center gap-1"><input type="checkbox" checked={f.email_listing_types.includes(k)} onChange={() => toggleIn('email_listing_types', k)} /> {l}</label>)}
                    <span className="text-[11px] text-gray-500">The others still show on this page; they are just not emailed.</span>
                </div>
            </div>
            <div className="flex justify-end gap-2">
                <button type="button" onClick={onCancel} className="rounded border border-gray-300 px-4 py-2 text-sm">Cancel</button>
                <button disabled={saving} className="rounded bg-slate-800 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">
                    {saving ? 'Saving…' : initial ? 'Save' : 'Start search'}
                </button>
            </div>
        </form>
    );
};

// ---------------------------------------------------------------------------
// Results
// ---------------------------------------------------------------------------
export const FsboBadge = ({ className = '' }) => (
    <span title={FSBO_HINT} className={`whitespace-nowrap rounded bg-amber-200 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-amber-900 ${className}`}>By owner</span>
);

const AddressCell = ({ c }) => (
    <>
        <p className="font-semibold text-gray-900">
            {c.is_new && <span className="mr-1 rounded bg-yellow-300 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-yellow-900">New</span>}
            {c.seller_type === 'owner' && <FsboBadge className="mr-1" />}
            <AddressLink c={c} className="text-blue-700 underline decoration-blue-300 hover:decoration-blue-700 print:text-gray-900 print:no-underline" />
        </p>
        <LinkNote c={c} className="text-[10px] text-gray-500 print:hidden" />
        {c.seller_type === 'owner' && (
            <p className="text-[10px] text-amber-800">
                Lead: owner selling without an agent{c.agent_phone ? ` · phone on the listing: ${c.agent_phone}` : ''} · check Do-Not-Call before phoning
            </p>
        )}
        <LookupLinks c={c} uses={RES_USES} intent={c.listing_type === 'sale' ? 'purchase' : 'lease'} />
    </>
);

const CityGroups = ({ rows, kit, searchId, onChange, canEdit }) => {
    const groups = groupByCity(rows);
    const { StatusSelect, NotesCell } = kit;
    if (!groups.length) return <p className="py-6 text-center text-sm text-gray-500">Nothing matches these filters.</p>;
    return (
        <div className="space-y-5">
            {groups.map((g) => (
                <section key={g.label} className="break-inside-avoid rounded border border-gray-200 bg-white">
                    <h3 className="flex flex-wrap items-baseline gap-2 border-b border-gray-200 bg-gray-50 px-3 py-2 text-sm font-bold text-gray-900">
                        {g.label}
                        <span className="text-xs font-normal text-gray-500">
                            {g.list.length} home{g.list.length === 1 ? '' : 's'}
                            {' · '}{g.list.filter((c) => c.listing_type === 'sale').length} for sale · {g.list.filter((c) => c.listing_type === 'rent').length} for rent
                            {g.list.some((c) => c.seller_type === 'owner') && <> · <span className="font-semibold text-amber-800">{g.list.filter((c) => c.seller_type === 'owner').length} by owner</span></>}
                            {g.list.some((c) => c.is_new) && <> · <span className="font-semibold text-yellow-800">{g.list.filter((c) => c.is_new).length} new</span></>}
                        </span>
                    </h3>
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-left text-[10px] uppercase tracking-wide text-gray-500">
                                    <th className="px-3 py-1">Address</th><th className="px-2 py-1">Beds / baths</th><th className="px-2 py-1">Price</th>
                                    <th className="px-2 py-1">Sale / rent</th><th className="px-2 py-1">First seen</th><th className="px-2 py-1">Status</th><th className="px-2 py-1">Notes</th>
                                </tr>
                            </thead>
                            <tbody>
                                {g.list.map((c) => (
                                    <tr key={c.candidate_id} className={`border-t border-gray-100 align-top ${PARKED.includes(c.status) ? 'bg-gray-50 text-gray-500' : ''}`}>
                                        <td className="px-3 py-2"><AddressCell c={c} /></td>
                                        <td className="whitespace-nowrap px-2 py-2 font-mono text-[12px]">{bedsBaths(c)}{c.property_kind && <span className="block font-sans text-[10px] text-gray-500">{KIND_LABEL[c.property_kind] || c.property_kind}</span>}</td>
                                        <td className="whitespace-nowrap px-2 py-2 font-mono text-[12px]">{c.price_display || '—'}</td>
                                        <td className="px-2 py-2">{c.listing_type
                                            ? <span className={`whitespace-nowrap rounded px-1.5 py-0.5 text-[10px] font-semibold ${c.listing_type === 'sale' ? 'bg-emerald-100 text-emerald-800' : 'bg-sky-100 text-sky-800'}`}>{LISTING_LABEL[c.listing_type]}</span>
                                            : <span className="text-xs text-gray-400">—</span>}
                                            {c.seller_type === 'owner' && <span className="mt-1 block text-[10px] font-semibold text-amber-800">by owner</span>}</td>
                                        <td className="whitespace-nowrap px-2 py-2 text-[11px] text-gray-600">{c.first_seen_at ? new Date(c.first_seen_at).toLocaleDateString() : '—'}
                                            {c.emailed_at && <span className="block text-[10px] text-gray-400" title={fmtDate(c.emailed_at)}>emailed</span>}</td>
                                        <td className="px-2 py-2"><StatusSelect searchId={searchId} c={c} onChange={onChange} canEdit={canEdit} /></td>
                                        <td className="px-2 py-2"><NotesCell searchId={searchId} c={c} onChange={onChange} canEdit={canEdit} /></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </section>
            ))}
        </div>
    );
};

export const ResidentialDetail = ({ data, searchId, canEdit, onBack, onReload, onListChanged, kit }) => {
    const apiClient = useApiClient();
    const [editing, setEditing] = useState(false);
    const [tab, setTab] = useState('results');
    const [f, setF] = useState({ listing_type: '', seller: '', pair: '', town: '', status: '', newOnly: false, showParked: false, q: '' });
    const { search, residential: rc, candidates, counts, activity, email } = data;

    const put = useCallback(async (body) => {
        try { await apiClient.put(`${API}/${searchId}`, body); await onReload(); onListChanged(); }
        catch (err) { alert(err.message || 'Could not save.'); }
    }, [apiClient, searchId, onReload, onListChanged]);
    const runNow = async () => {
        try { const r = await apiClient.post(`${API}/${searchId}/run-now`, {}); alert(r.message); onReload(); }
        catch (err) { alert(err.message || 'Could not queue the search.'); }
    };
    const testEmail = async () => {
        try { const r = await apiClient.post(`${API}/${searchId}/test-email`, {}); alert(r.message); onReload(); }
        catch (err) { alert(err.message || 'Could not send the test email.'); }
    };
    const exportCsv = async () => {
        const qs = new URLSearchParams({ ...(f.listing_type ? { listing_type: f.listing_type } : {}), ...(f.seller ? { seller: f.seller } : {}), ...(f.pair ? { pair: f.pair } : {}),
            ...(f.town ? { town: f.town } : {}), ...(f.status ? { status: f.status } : {}), ...(f.newOnly ? { new: '1' } : {}), ...(f.showParked ? { parked: '1' } : {}) });
        try { await kit.downloadExport(`${API}/${searchId}/export.csv?${qs}`, `homes-search-${searchId}-${new Date().toISOString().slice(0, 10)}.csv`); }
        catch (err) { alert(err.message); }
    };
    const shown = applyFilters(candidates, f);
    const towns = groupByCity(candidates).map((g) => g.city).filter((t) => t !== 'Town not known');
    const setFil = (k) => (e) => setF({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value });

    return (
        <div className="space-y-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                    <button onClick={onBack} className="text-xs text-indigo-600 hover:underline print:hidden">← My searches</button>
                    <h2 className="text-xl font-bold text-gray-900">{search.title}</h2>
                    <div className="mt-1 flex flex-wrap gap-1">
                        <span className="rounded-full border border-emerald-700 bg-emerald-700 px-2 py-0.5 text-xs text-white">Residential</span>
                        {rc.pairs.map((p) => <span key={`${p.beds}/${p.baths}`} className="rounded-full border border-gray-300 px-2 py-0.5 text-xs text-gray-700">{pairLabel(p)}</span>)}
                        {rc.kinds.map((k) => <span key={k} className="rounded-full border border-gray-300 px-2 py-0.5 text-xs text-gray-700">{KIND_LABEL[k]}</span>)}
                        {rc.listing_types.map((k) => <span key={k} className="rounded-full border border-gray-300 px-2 py-0.5 text-xs text-gray-700">{LISTING_LABEL[k]}</span>)}
                        {rc.include_fsbo && rc.listing_types.includes('sale') && <span className="rounded-full border border-amber-300 bg-amber-50 px-2 py-0.5 text-xs text-amber-900">incl. for sale by owner</span>}
                    </div>
                    <p className="mt-1 text-xs text-gray-600">
                        {rc.towns.length} towns: {rc.towns.join(' · ')}
                    </p>
                    <p className="text-xs text-gray-600">
                        {rc.price_min || rc.price_max ? `$${rc.price_min || '—'} – $${rc.price_max || '—'} · ` : 'Any price · '}
                        bathrooms {rc.exact_baths ? 'exactly' : 'at least'} as listed
                        {' · '}runs {Number(search.sweep_enabled) ? FREQUENCY_LABEL[search.sweep_frequency] || search.sweep_frequency : 'never (automatic search off)'}
                        {data.next_run && Number(search.sweep_enabled) ? ` · next about ${fmtDate(data.next_run)}` : ''}
                        {' · '}last run {fmtDate(search.last_swept_at)}
                    </p>
                    <p className="text-xs text-gray-600">
                        Email new finds: {email.enabled ? <strong className="text-green-700">on</strong> : <strong className="text-gray-700">off</strong>}
                        {email.enabled && email.recipients.length ? ` → ${email.recipients.join(', ')}` : ''}
                        {email.enabled && !email.site_enabled && <span className="ml-1 text-red-700">(the site switch is OFF — nothing is sent until it is on)</span>}
                    </p>
                    {search.last_sweep_summary && <p className="text-xs text-gray-500">Last run: {search.last_sweep_summary}</p>}
                    {search.sweep_requested_at && <p className="text-xs text-indigo-700">Run requested {fmtDate(search.sweep_requested_at)} — it starts at the office computer's next check (about every 30 minutes).</p>}
                    {rc.notes && <p className="text-xs text-gray-600">{rc.notes}</p>}
                </div>
                <div className="flex flex-wrap items-center gap-2 print:hidden">
                    <span className={`rounded px-2 py-1 text-xs font-semibold ${search.status === 'active' ? 'bg-green-100 text-green-800' : search.status === 'paused' ? 'bg-amber-100 text-amber-800' : 'bg-gray-200 text-gray-700'}`}>{search.status}</span>
                    {canEdit && search.status !== 'closed' && (
                        <>
                            <label className="flex items-center gap-1 rounded border border-gray-300 px-2 py-1 text-xs" title="The office computer runs the search by itself">
                                <input type="checkbox" checked={!!Number(search.sweep_enabled)} onChange={(e) => put({ sweep_enabled: e.target.checked })} />
                                Automatic search
                            </label>
                            <label className="flex items-center gap-1 rounded border border-amber-300 bg-amber-50 px-2 py-1 text-xs" title="Also look for homes for sale by owner (FSBO.com, ForSaleByOwner.com, Zillow by owner, Craigslist by owner)">
                                <input type="checkbox" checked={!!rc.include_fsbo} onChange={(e) => put({ include_fsbo: e.target.checked })} />
                                Include For Sale By Owner
                            </label>
                            <select value={search.sweep_frequency} onChange={(e) => put({ sweep_frequency: e.target.value })} className="rounded border border-gray-300 px-2 py-1 text-xs"><FrequencyOptions /></select>
                            <button onClick={runNow} disabled={!!search.sweep_requested_at || search.status !== 'active' || !Number(search.sweep_enabled)}
                                    className="rounded bg-slate-800 px-3 py-1 text-xs font-semibold text-white disabled:opacity-50">
                                {search.sweep_requested_at ? 'Run queued…' : 'Run search now'}
                            </button>
                            <button onClick={testEmail} className="rounded border border-gray-300 px-3 py-1 text-xs" title="The latest finds, to your own email address only. Marks nothing as sent.">Send test email to me</button>
                            <button onClick={() => setEditing(true)} className="rounded border border-gray-300 px-3 py-1 text-xs">Edit</button>
                            {search.status === 'active'
                                ? <button onClick={() => put({ status: 'paused' })} className="rounded border border-gray-300 px-3 py-1 text-xs">Pause</button>
                                : <button onClick={() => put({ status: 'active' })} className="rounded border border-gray-300 px-3 py-1 text-xs">Resume</button>}
                            <button onClick={() => { if (window.confirm('Close this search? The searching and emails stop; the results stay.')) put({ status: 'closed' }); }}
                                    className="rounded border border-gray-300 px-3 py-1 text-xs">Close</button>
                        </>
                    )}
                    {canEdit && search.status === 'closed' && <button onClick={() => put({ status: 'active' })} className="rounded border border-gray-300 px-3 py-1 text-xs">Reopen</button>}
                </div>
            </div>

            <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
                {[['Homes found', counts.total], ['For sale', counts.sale], ['By owner', counts.fsbo || 0], ['For rent', counts.rent], ['New (3 days)', counts.fresh]].map(([k, n]) => (
                    <div key={k} className="rounded border border-gray-200 bg-white p-2">
                        <p className="text-[10px] uppercase tracking-wide text-gray-500">{k}</p>
                        <p className="text-xl font-bold text-gray-900">{n}</p>
                    </div>
                ))}
            </div>

            <div className="flex gap-1 border-b border-gray-200 print:hidden">
                {[['results', 'Results by city'], ['activity', 'History']].map(([k, l]) => (
                    <button key={k} onClick={() => setTab(k)}
                            className={`rounded-t px-3 py-1.5 text-sm font-semibold ${tab === k ? 'bg-slate-800 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>{l}</button>
                ))}
            </div>

            {tab === 'results' && (
                <div className="space-y-3">
                    <div className="flex flex-wrap items-center gap-2 text-xs print:hidden">
                        <select value={f.listing_type} onChange={setFil('listing_type')} className="rounded border border-gray-300 px-2 py-1">
                            <option value="">Sale and rent</option><option value="sale">For sale</option><option value="rent">For rent</option>
                        </select>
                        <select value={f.seller} onChange={setFil('seller')} className="rounded border border-gray-300 px-2 py-1" title="Who is selling">
                            <option value="">All sellers</option><option value="agent">Agent-listed</option><option value="owner">By owner (FSBO)</option>
                        </select>
                        <select value={f.pair} onChange={setFil('pair')} className="rounded border border-gray-300 px-2 py-1">
                            <option value="">Any beds / baths</option>
                            {rc.pairs.map((p) => <option key={`${p.beds}/${p.baths}`} value={`${p.beds}/${p.baths}`}>{pairLabel(p)}{rc.exact_baths ? '' : '+'}</option>)}
                        </select>
                        <select value={f.town} onChange={setFil('town')} className="rounded border border-gray-300 px-2 py-1">
                            <option value="">Every city</option>{towns.map((t) => <option key={t} value={t}>{t}</option>)}
                        </select>
                        <select value={f.status} onChange={setFil('status')} className="rounded border border-gray-300 px-2 py-1">
                            <option value="">Any status</option>{CAND_STATUSES.map((s) => <option key={s} value={s}>{STATUS_LABEL[s] || s}</option>)}
                        </select>
                        <label className="flex items-center gap-1"><input type="checkbox" checked={f.newOnly} onChange={setFil('newOnly')} /> New only</label>
                        <label className="flex items-center gap-1"><input type="checkbox" checked={f.showParked} onChange={setFil('showParked')} /> Show rejected / off market</label>
                        <input type="search" value={f.q} onChange={setFil('q')} placeholder="Search addresses, notes…" className="min-w-[10rem] flex-1 rounded border border-gray-300 px-2 py-1" />
                        <span className="text-gray-500">{shown.length} of {candidates.length}</span>
                        <button onClick={exportCsv} className="rounded border border-gray-300 bg-slate-100 px-2 py-1">CSV</button>
                        <button onClick={() => window.print()} className="rounded border border-gray-300 px-2 py-1">Print</button>
                    </div>
                    <CityGroups rows={shown} kit={kit} searchId={searchId} onChange={onReload} canEdit={canEdit} />
                    {!candidates.length && (
                        <p className="rounded bg-gray-50 p-3 text-sm text-gray-600">
                            Nothing found yet. The office computer runs this search {FREQUENCY_LABEL[search.sweep_frequency] || search.sweep_frequency} while it is on,
                            looking on Zillow, Realtor.com, Redfin, Apartments.com and ForRent through web-search results
                            {rc.include_fsbo && rc.listing_types.includes('sale') ? ', plus FSBO.com, ForSaleByOwner.com and Craigslist for homes for sale by owner' : ''}.
                        </p>
                    )}
                </div>
            )}

            {tab === 'activity' && (
                <ul className="space-y-1 text-xs text-gray-700">
                    {activity.map((a) => (
                        <li key={a.activity_id || `${a.created_at}-${a.event}`}><span className="text-gray-500">{fmtDate(a.created_at)}</span> · {a.event.replace(/_/g, ' ')}{a.detail ? ` — ${a.detail}` : ''}{a.actor ? ` (${a.actor})` : ''}</li>
                    ))}
                    {!activity.length && <li className="text-gray-500">No history yet.</li>}
                </ul>
            )}

            <Modal open={editing} onClose={() => setEditing(false)} title="Edit search" size="lg">
                {editing && <ResidentialForm initial={search} onCancel={() => setEditing(false)}
                                             onSaved={() => { setEditing(false); onReload(); onListChanged(); }} />}
            </Modal>
        </div>
    );
};
